import GanttChartTabBar from "../components/ganttChart/GanttChartTabBar";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import VerticalGanttWrapper from "../components/ganttChart/VerticalGanttWrapper";
import TaskDrawerContent from "../components/taskDrawer/TaskDrawerContent";
import GanttChartConfigBar from "../components/ganttChart/GanttChartConfigBar";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "../components/UI/Spinner";
import MainContentWrapper from "../components/UI/pageStructure/MainContentWrapper";
import NexusExportErrorModal from "../components/header/NexusExportErrorModal";
import api, { API_PATHS } from "../service/api";
import { NexusSendResult, SendPlanToNexusBody } from "../types/apiTypes";
import { setNotification } from "../store/toastNotifications";
import { closeNexusErrorModal } from "../store/overlay";
import { closeDrawer } from "../store/drawer";
import labels from "../utils/labels";
import Drawer from "../components/drawer/Drawer";
import DragAndDropStatusBar from "../components/ganttChart/dragAndDrop/DragAndDropStatusBar";
import statusBarsStyles from "../styles/statusBars.module.css";
import SessionWarningModal from "../components/ganttChart/dragAndDrop/SessionWarningModal";
import useDataSelector from "../hooks/useDataSelector";
import EmployeeDrawerContent from "../components/employeeDrawer/EmployeeDrawerContent";
import { useToggleGanttChartSpinner } from "../hooks/useToggleSpinner";
import { useSolutionPlanView } from "../hooks/useSolutionPlanView";
import { useFetchOnQueryChange } from "../hooks/useFetchOnQueryChange";
import GanttChartSearchBar from "../components/search/GanttChartSearchBar";
import UnsavedEmployeeEditsModal from "../components/employeeDrawer/UnsavedEmployeeEditsModal";
import DeleteWorkTimeModal from "../components/employeeDrawer/DeleteWorktimeModal";
import EmployeeSettingsService from "../service/employeeSettingsService";
import {
  clearEmployeeTransportEdit,
  clearEmployeeWorkTimeEdit,
} from "../store/employeeSettings";
import { updateEmployeeWorkTime } from "../store/ganttChart";
import { getGanttChartData } from "../store/ganttChart/action";
import { useQueryParams } from "../hooks/useQueryParams";
import ContingencySettingsTab from "../components/contingencySettings/ContingencySettingsTab";

export default function GanttChartPage() {
  const dispatch = useAppDispatch();
  useToggleGanttChartSpinner(); //Handle showing spinner when state is loading.
  useFetchOnQueryChange();
  const { dataToUse } = useDataSelector();

  const { isDrawerOpen, drawerType, selectedEmployee, selectedTask } =
    useAppSelector((state) => state.drawerSlice);
  const { nexusErrorModal } = useAppSelector((state) => state.overlaySlice);
  const {
    ganttChartData,
    showSpinner,
    showSpinnerOnCustomData,
    error,
    selectedGanttChartTab,
    isContingencyMode,
    isContingencyPlanPending,
  } = useAppSelector((state) => state.ganttChartSlice);
  const { isDragAndDropSessionWarningModalOpen } = useAppSelector(
    (state) => state.dragAndDropSlice,
  );

  const [hasEmployeeUnsavedEdits, setHasEmployeeUnsavedEdits] = useState(false);
  const [isUnsavedEditsModalOpen, setIsUnsavedEditsModalOpen] = useState(false);
  const worktimeSaveRef = useRef<(() => Promise<void>) | null>(null);
  const transportSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [isDeleteWorkTimeModalOpen, setIsDeleteWorkTimeModalOpen] =
    useState(false);

  const planViewState = useSolutionPlanView();
  const queryKey = useQueryParams();

  useEffect(() => {
    const ganttChartElement = document.getElementById("outer-scroll-wrapper");
    if (selectedGanttChartTab === "Custom" && ganttChartElement)
      ganttChartElement.style.userSelect = "none";
    else if (ganttChartElement) ganttChartElement.style.userSelect = "";
  }, [selectedGanttChartTab]);

  /**
   * Handle sending to Nexus ignoring any misalignments between the Pondoo plan and the state of Nexus
   */
  const forceSendToNexus = async () => {
    if (!ganttChartData?.idScenario) return;

    try {
      const body: SendPlanToNexusBody = {
        idScenario: ganttChartData.idScenario,
        forceSend: true,
      };
      const response = await api.post<NexusSendResult>(
        API_PATHS.postNexusPlan,
        body,
      );

      if (response.status === 200 && response.data?.success) {
        dispatch(closeNexusErrorModal());
        dispatch(
          setNotification({
            message: labels.nexusExportSuccess,
            type: "send_to_nexus",
            variant: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: response.data?.message || labels.nexusExportErrorGeneral,
            type: "send_to_nexus",
          }),
        );
      }
    } catch (error) {
      dispatch(
        setNotification({
          message: labels.nexusExportErrorGeneral,
          type: "send_to_nexus",
        }),
      );
    }
  };

  async function handleConfirmDeleteWorkTime() {
    if (!selectedEmployee) return;
    const shiftId = selectedEmployee.employeeShiftId;
    const deletedWorkTimeStart =
      ganttChartData?.employees.find((e) => e.shiftSchedule.idShift === shiftId)
        ?.shiftStartTime ?? "";
    try {
      await EmployeeSettingsService.deleteEmployeeWorkTime(shiftId, dispatch);
      dispatch(clearEmployeeTransportEdit({ shiftId }));
      dispatch(clearEmployeeWorkTimeEdit({ shiftId }));
      dispatch(
        updateEmployeeWorkTime({
          shiftId,
          shiftStartTime: deletedWorkTimeStart,
          shiftEndTime: deletedWorkTimeStart,
        }),
      );
      await EmployeeSettingsService.clearTransportSegments(shiftId, dispatch);

      // Clearing arbejdstid changes the backend's KPI recompute (e.g. the
      // employee drops out of the Medarbejdere count), so refresh the
      // visible Gantt the same way an arbejdstid edit does.
      if (
        queryKey.filterId != null &&
        queryKey.date != null &&
        queryKey.solutionType != null
      ) {
        await dispatch(
          getGanttChartData({
            filterId: queryKey.filterId,
            date: queryKey.date,
            solutionType: queryKey.solutionType,
          }),
        );
      }

      setIsDeleteWorkTimeModalOpen(false);
    } catch {}
  }

  return (
    <>
      <section className="flex flex-col relative">
        <div className="flex justify-end min-h-[43px]">
          <GanttChartTabBar />
          {ganttChartData && planViewState.status === "ready" && (
            <GanttChartSearchBar
              employees={ganttChartData.employees}
              unplannedTasks={
                ganttChartData.generalGanttChartInfo.unplannedTasks.workTasks
              }
            />
          )}
        </div>

        {/* TODO: figure out how to take the first element in the array passed to the tabs bar */}
        <MainContentWrapper
          classes="flex flex-col"
          firstTab="Original"
          selectedTab={selectedGanttChartTab}
        >
          {isContingencyMode ? (
            <ContingencySettingsTab />
          ) : (
            <>
              <GanttChartConfigBar />
              {["missing", "error"].includes(planViewState.status) &&
                !showSpinner &&
                selectedGanttChartTab !== "Custom" && (
                  <div className="flex flex-col gap-[10px] items-center justify-center h-full">
                    {planViewState.status === "error" ? (
                      <p style={{ color: "var(--col-error-3)" }}>
                        {planViewState.errorMessage ?? error}
                      </p>
                    ) : (
                      <p>
                        <b>{labels.missingPlanToday}</b> <br />
                        {labels.goToNexusToSendData}
                      </p>
                    )}
                  </div>
                )}
              {showSpinner && (
                <>
                  <div className="flex items-center justify-center flex-col w-full h-full">
                    <Spinner
                      fullSize
                      infoText={
                        isContingencyPlanPending
                          ? labels.contingencyCalculating
                          : undefined
                      }
                    />
                  </div>
                </>
              )}
              {showSpinnerOnCustomData && <Spinner fullSize />}
              {((selectedGanttChartTab !== "Custom" &&
                dataToUse &&
                planViewState.status === "ready") ||
                (selectedGanttChartTab === "Custom" && dataToUse)) &&
                !showSpinner && <VerticalGanttWrapper />}
            </>
          )}
        </MainContentWrapper>
      </section>

      {isDrawerOpen && drawerType === "Task" && selectedTask && (
        <Drawer
          onClose={() => dispatch(closeDrawer())}
          heading={`${labels.visit}: ${selectedTask.task.client.description}`}
        >
          <TaskDrawerContent />
        </Drawer>
      )}
      {isDrawerOpen && drawerType === "Employee" && selectedEmployee && (
        <Drawer
          onClose={() => {
            hasEmployeeUnsavedEdits
              ? setIsUnsavedEditsModalOpen(true)
              : dispatch(closeDrawer());
          }}
          heading={
            ganttChartData?.employees.find(
              (e) =>
                e.shiftSchedule.idShift === selectedEmployee.employeeShiftId,
            )?.fullName ?? ""
          }
        >
          <EmployeeDrawerContent
            onEmployeeUnsavedEdit={setHasEmployeeUnsavedEdits}
            onDeleteWorkTimeRequest={() => {
              setIsDeleteWorkTimeModalOpen(true);
            }}
            worktimeSaveRef={worktimeSaveRef}
            transportSaveRef={transportSaveRef}
          />
        </Drawer>
      )}
      <div className={statusBarsStyles.statusBarContainer}>
        {selectedGanttChartTab === "Custom" && <DragAndDropStatusBar />}
      </div>

{/* Error modal shown when sending to Nexus in the event of failure */}
      {nexusErrorModal && (
        <NexusExportErrorModal
          isOpen={!!nexusErrorModal}
          failedTasks={nexusErrorModal.failedTasks}
          message={nexusErrorModal.message}
          onForceSend={forceSendToNexus}
        />
      )}
      {/* Warning modal when user closes the edit tab without saving first. */}
      {isDragAndDropSessionWarningModalOpen && <SessionWarningModal />}
      {/* Warning modal when user closes the Employee drawer without saving first */}
      {isUnsavedEditsModalOpen && (
        <UnsavedEmployeeEditsModal
          onKeepEditing={() => setIsUnsavedEditsModalOpen(false)}
          onDiscard={() => {
            setIsUnsavedEditsModalOpen(false);
            setHasEmployeeUnsavedEdits(false);
            dispatch(closeDrawer());
          }}
          onSaveAndClose={async () => {
            await Promise.all(
              [worktimeSaveRef.current, transportSaveRef.current]
                .filter(Boolean)
                .map((fn) => fn!()),
            );
            setIsUnsavedEditsModalOpen(false);
            setHasEmployeeUnsavedEdits(false);
            dispatch(closeDrawer());
          }}
        />
      )}
      {isDeleteWorkTimeModalOpen && (
        <DeleteWorkTimeModal
          onDelete={handleConfirmDeleteWorkTime}
          onDiscard={() => setIsDeleteWorkTimeModalOpen(false)}
        />
      )}
    </>
  );
}
